import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {BluechatLoad} from '../shared/bluechat-load.model';
import {BluechatDelivery} from '../shared/bluechat-delivery.model';
import {NavigationService} from '../../shared/services/navigation.service';
import {finalize, switchMap, take, tap} from 'rxjs/operators';
import {LoadManagementService} from '../shared/load-management.service';
import {ActivatedRoute} from '@angular/router';
import {AuthenticationService} from '../../shared/services/authentication.service';
import {BluechatNoteModalDelivery} from '../models/BluechatNoteModalDelivery';
import {ModalService} from '../../shared/services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {UserBluechatConfiguration} from '../search/bluechat-search.component';
import {forkJoin} from 'rxjs';
import {BluechatService} from '../bluechat.service';
import {Location} from '@angular/common';
import { NavigationLinkService } from '../../shared/services/navigation-link.service';


@Component({
  selector: 'app-bluechat-load-detail',
  templateUrl: './bluechat-load-detail.component.html',
  styleUrls: ['./bluechat-load-detail.component.scss']
})
export class BluechatLoadDetailComponent implements OnInit {

  @Input() public loadNumber: string;
  @Input() public userCanCreateNote = false;
  @Input() public carrierHasBluechat = false;
  @Output() public close = new EventEmitter<void>();
  @Output() public createNote = new EventEmitter<BluechatLoad>();
  @ViewChild('noteCreationModal') noteCreationModal: TemplateRef<any>;

  public userBluechatConfig: UserBluechatConfiguration            = {
    assignedCompanies: [],
    assignedProcesses: [],
  };

  public loading = false;

  public load: BluechatLoad;

  public loadStatusAllowNoteCreation: boolean;
  public bluechatDelivery: BluechatNoteModalDelivery;

  public isSelfCarrier = false;

  constructor(private navigationService: NavigationService,
              private loadManagementService: LoadManagementService,
              private route: ActivatedRoute,
              public link: NavigationLinkService,
              private authService: AuthenticationService,
              private modalService: ModalService,
              private translateService: TranslateService,
              private bluechatService: BluechatService,
              private location: Location,
  ) {}

  public ngOnInit() {
    this.userCanCreateNote = this.authService.canSubmitBluechat();
    this.isSelfCarrier = this.authService.canManageDrivers();

    if (this.authService.canSubmitAllBluechatNotes()) {
      forkJoin([this.bluechatService.getSelfAssignedCompanies(), this.bluechatService.getSelfAssignedProcesses()])
          .subscribe(([companies, processes]) => {
            this.userBluechatConfig = {
              assignedCompanies: companies,
              assignedProcesses: processes,
            }
          });
    } else if (this.authService.canSubmitCarrierBluechatNotes()) {
      const currentCarrier = this.authService.getUserProfile().carrier;
      forkJoin([
        this.bluechatService.getCompany(currentCarrier.bluechatCarrierCode),
        this.bluechatService.getProcesses([currentCarrier.bluechatCarrierCode])
      ]).subscribe(([company, processes]) => {
        this.userBluechatConfig = {
          assignedCompanies: [company],
          assignedProcesses: processes[currentCarrier.bluechatCarrierCode],
        }
      });
    }

    this.refreshLoad();
  }

  public refreshLoad() {
    this.loading = true;

    this.route.paramMap.pipe(
        take(1),
        switchMap(params => this.loadManagementService.getById(params.get('loadId'))),
        tap(load => this.loadStatusAllowNoteCreation = this.loadManagementService.doesLoadStatusAllowsNoteCreation(load.status)),
        tap(load => this.carrierHasBluechat = load.bluechatCarrierCode !== null),
        finalize(() => this.loading = false),
    )
    .subscribe(load => this.load = load);
  }

  public transformToLoadAndEmit(delivery: BluechatDelivery) {

    this.bluechatDelivery = new BluechatNoteModalDelivery();
    this.bluechatDelivery.deliveryNumber = delivery.deliveryNumber;
    this.bluechatDelivery.carrierName = this.load.carrierName;
    this.bluechatDelivery.carrierBlueyonderCode = this.load.blueyonderCarrierCode;
    this.bluechatDelivery.carrierBluechatCode = this.load.bluechatCarrierCode;
    this.bluechatDelivery.carrierHasBluechatEnabled = this.load.bluechatCarrierCode != null;
    this.bluechatDelivery.carrierExistsOnCmaWeb = this.load.blueyonderCarrierCode != null;

    this.modalService.open({
      title: this.translateService.instant(
          'bluechat.note-creation.title-single-delivery',
          { deliveryNumber: this.bluechatDelivery?.deliveryNumber },
      ),
      closeButton: {
        displayed: false,
        text: null,
      },
      validateButton: {
        displayed: false,
        text: null,
      },
      content: this.noteCreationModal,
    }).subscribe();

  }

  public navigateToLoadTracking(delivery: BluechatDelivery) {
    this.close.emit();
    this.navigationService.realtimeTracking()
      .map(this.load.carrierId, delivery.tripId)
      .go();
  }

  public goBack() {
    this.location.back()
  }
}
